import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Camera, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { postOrQueue } from "@/lib/offlineQueue";
import { toast } from "sonner";

const todayToronto = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Toronto" });
const nowTorontoHM = () =>
  new Date().toLocaleTimeString("en-GB", { timeZone: "America/Toronto", hour12: false, hour: "2-digit", minute: "2-digit" });

const MAX_PHOTOS = 6;

/**
 * Rip-out report form — when another trade rips apart Elite's work, staff
 * log it on the spot with photos, the project (its saved location is used
 * as the address) and when it happened. Admins/owners/managers get an
 * in-app + push alert and the office gets an email with the photos
 * attached. Used by the Rip-outs sub-tab (staff) and the Requests tab
 * (everyone).
 */
const RipOutFormDialog = ({ onClose, onSubmitted, testid = "ripout-form", inline = false }) => {
  const [jobs, setJobs] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [date, setDate] = useState(todayToronto());
  const [time, setTime] = useState(nowTorontoHM());
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        // List every job — rip-outs are sometimes reported on jobs that were
        // already closed out. Active jobs sort first, then A-Z.
        const isLive = (p) => (p.status || "Active") === "Active" || p.status === "In Progress";
        setJobs((data || []).slice().sort((a, b) => Number(isLive(b)) - Number(isLive(a)) || (a.name || "").localeCompare(b.name || "")));
      } catch { /* offline — job list unavailable */ }
    })();
  }, []);

  const addPhotos = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setPhotos((prev) => [...prev, ...list].slice(0, MAX_PHOTOS));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!projectId) { toast.error("Select the job this rip-out belongs to"); return; }
    const job = jobs.find((j) => j.project_id === projectId);
    const address = (job?.location || "").trim();
    if (!address) { toast.error("This job has no location saved — add one on the Projects tab first"); return; }
    if (!date) { toast.error("Pick the date it happened"); return; }
    if (photos.length === 0) { toast.error("Add at least one photo"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("address", address);
      fd.append("occurred_date", date);
      fd.append("occurred_time", time);
      fd.append("notes", notes.trim());
      photos.forEach((p) => fd.append("photos", p));
      const res = await postOrQueue("/rip-outs", fd, "rip-out report");
      if (res.queued) toast.warning("No signal — the rip-out (with photos) is saved and will be sent when you're back online");
      else toast.success("Rip-out logged — the office has been notified");
      onSubmitted?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log the rip-out");
    } finally { setBusy(false); }
  };

  const form = (
    <>
      <div className="space-y-3 overflow-y-auto p-4">
        <p className="text-xs leading-relaxed text-white/60">
          Another trade ripped apart our work? Photograph it and log it here — the office is emailed and
          admins are alerted right away so we can back-charge.
        </p>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Job *</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid={`${testid}-project`}
              className="h-10 w-full rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
            >
              <option value="">— Select the job —</option>
              {jobs.map((j) => {
                const live = (j.status || "Active") === "Active" || j.status === "In Progress";
                return <option key={j.project_id} value={j.project_id}>{live ? j.name : `${j.name} (${j.status || "Inactive"})`}</option>;
              })}
            </select>
            <p className="mt-1 text-[10px] text-white/40">The job's saved location is used as the address.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Date *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`${testid}-date`}
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Time</label>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid={`${testid}-time`}
                className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-white focus:border-[#1a3fd1] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="What was ripped out, who did it, anything else…"
              data-testid={`${testid}-notes`}
              className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-white focus:border-[#1a3fd1] focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Photos * (up to {MAX_PHOTOS})</label>
            <div className="flex flex-wrap gap-2" data-testid={`${testid}-photos`}>
              {photos.map((p, i) => (
                <div key={`${p.name}-${i}`} className="relative h-16 w-16 overflow-hidden rounded-md border border-white/15">
                  <img src={URL.createObjectURL(p)} alt="" className="h-full w-full object-cover" />
                  <button
                    onClick={() => setPhotos((prev) => prev.filter((_, x) => x !== i))}
                    aria-label="Remove photo"
                    data-testid={`${testid}-photo-remove-${i}`}
                    className="absolute right-0.5 top-0.5 rounded bg-black/70 p-0.5 text-white/80 hover:text-red-400"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-md border border-dashed border-white/25 bg-white/5 text-white/50 hover:border-[#1a3fd1] hover:text-[#8fa4f0]" data-testid={`${testid}-photo-add`}>
                  <Plus className="h-5 w-5" />
                  <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" data-testid={`${testid}-photo-input`}
                    onChange={(e) => addPhotos(e.target.files)} />
                </label>
              )}
            </div>
          </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-white/10 px-4 py-3">
        {!inline && (
          <button onClick={onClose} data-testid={`${testid}-cancel`} className="rounded-md border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/70">Cancel</button>
        )}
        <button
          onClick={submit} disabled={busy}
          data-testid={`${testid}-submit`}
          className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Log rip-out
        </button>
      </div>
    </>
  );

  if (inline) {
    return (
      <div className="overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" data-testid={`${testid}-dialog`}>
        {form}
      </div>
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3" data-testid={`${testid}-dialog`}>
      <div className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-md border border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-300">
              <Camera className="h-4 w-4" />
            </div>
            <h3 className="text-base font-bold uppercase tracking-tight" data-testid={`${testid}-title`}>Log a rip-out</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="rounded border border-white/15 bg-white/5 p-1.5 text-white/70"><X className="h-4 w-4" /></button>
        </div>
        {form}
      </div>
    </div>,
    document.body,
  );
};

export default RipOutFormDialog;
